import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { FiEdit2 } from 'react-icons/fi';
import { AiOutlineBarChart } from 'react-icons/ai';
import { useAuth } from '../contexts/AuthContext';
import { systemsAPI } from '../services/api';
import { System, SystemsResponse } from '../types';
import { useNavigate, useParams } from 'react-router-dom';

// react-icons typings sometimes produce ReactNode return types causing JSX typing issues.
// Cast to `any` for safe JSX usage.
const BarChartIcon: any = AiOutlineBarChart;

const CustomerSystems: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: previewCustomerId } = useParams();
  const isPreview = !!previewCustomerId;
  const [systems, setSystems] = useState<System[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSystem, setEditingSystem] = useState<System | null>(null);
  const [formData, setFormData] = useState({
    systemName: '',
    description: '',
    location: '',
    installationDate: new Date().toISOString().split('T')[0]
  });

  const fetchSystems = useCallback(async () => {
    try {
      setLoading(true);
      const response: SystemsResponse = await systemsAPI.getAll(1, 100, '', isPreview ? String(previewCustomerId) : '');
      setSystems(response.systems);
    } catch (err) {
      setError('Failed to fetch systems');
    } finally {
      setLoading(false);
    }
  }, [isPreview, previewCustomerId]);

  useEffect(() => {
    fetchSystems();
  }, [fetchSystems]);

  const getStatusBadge = (status: string) => {
    const variants: { [k: string]: string } = {
      online: 'success',
      offline: 'secondary',
      maintenance: 'warning',
      error: 'danger',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const handleOpenEdit = (system: System) => {
    setEditingSystem(system);
    setFormData({
      systemName: system.systemName,
      description: system.description || '',
      location: system.location || '',
      installationDate: new Date(system.installationDate).toISOString().split('T')[0]
    });
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingSystem(null);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSystem) return;
    setError('');
    setSuccess('');
    try {
      await systemsAPI.update(editingSystem.id, {
        ...editingSystem,
        systemName: formData.systemName,
        description: formData.description,
        location: formData.location,
        installationDate: formData.installationDate,
        // keep immutable fields untouched: customerId, primaryProductId, gatewayId, systemCode
      });
      setSuccess('System updated successfully');
      await fetchSystems();
      setTimeout(() => handleCloseEdit(), 1200);
    } catch (err: any) {
      setError(err.message || 'Update failed');
    }
  };

  if (loading) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading systems...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" style={{ background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)' }}>
      <div style={{ width: '92%', maxWidth: '1600px', margin: '0 auto' }}>
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-lg" style={{ 
            background: 'linear-gradient(135deg, #E63946 0%, #DC2626 100%)',
            borderRadius: '20px',
            overflow: 'hidden'
          }}>
            <Card.Body className="p-4 p-md-5">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
                <div className="text-white mb-3 mb-sm-0">
                  <div className="text-uppercase small opacity-75 mb-2" style={{ letterSpacing: '2px', fontWeight: 600 }}>Systems Management</div>
                  <h2 className="mb-2 fw-bold" style={{ fontSize: '2rem' }}>{isPreview ? 'Customer View' : 'Your Systems'}</h2>
                  <p className="mb-0 opacity-90" style={{ fontSize: '1.05rem' }}>
                    {isPreview
                      ? 'Previewing customer systems as super admin.'
                      : <>Welcome {user?.name}. Monitor and manage your installations.</>}
                  </p>
                </div>
                <div className="text-white text-end">
                  <div className="display-4 fw-bold mb-1" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>{systems.length}</div>
                  <div className="small opacity-90">Total Systems</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible className="modern-card">{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible className="modern-card">{success}</Alert>}

      <Row className="g-4">
        {systems.map((s, index) => (
          <Col key={s.id} xs={12} md={6} lg={4}>
            <Card className="h-100 border-0 shadow-lg" style={{ 
              borderRadius: '16px',
              transform: 'translateY(0)',
              transition: 'all 0.3s ease',
              animation: `scaleIn 0.4s ease-out ${index * 0.1}s backwards`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}>
              <div style={{ 
                height: '6px', 
                background: s.status === 'online' 
                  ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' 
                  : s.status === 'maintenance' 
                  ? 'linear-gradient(90deg, #f59e0b 0%, #f97316 100%)'
                  : s.status === 'error'
                  ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(90deg, #1E3A8A 0%, #3B82F6 100%)',
                borderRadius: '16px 16px 0 0'
              }} />
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                        width: 40, 
                        height: 40, 
                        background: s.status === 'online' 
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                          : s.status === 'maintenance'
                          ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                          : 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}>
                        <span className="text-white fw-bold">🔌</span>
                      </div>
                      <div>
                        <Card.Title className="mb-0 fw-bold" style={{ fontSize: '1.2rem' }}>{s.systemName}</Card.Title>
                        <small className="text-muted" style={{ fontSize: '0.8rem' }}>{s.systemCode}</small>
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className={`badge rounded-pill px-3 py-2`} style={{ 
                      background: s.status === 'online' 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                        : s.status === 'maintenance'
                        ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                        : s.status === 'error'
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}>
                      {s.status === 'online' && '● '}{s.status}
                    </span>
                  </div>
                </div>
                <div className="mt-3" style={{ background: '#f8f9fa', borderRadius: '12px', padding: '16px' }}>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                          <td className="text-muted fw-medium py-2" style={{ width: '40%', border: 'none' }}>🏬 Client</td>
                          <td className="fw-semibold py-2" style={{ border: 'none' }}>{typeof s.customerId === 'object' ? (s.customerId as any).companyName : 'N/A'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                          <td className="text-muted fw-medium py-2" style={{ border: 'none' }}>📡 Gateway</td>
                          <td className="fw-semibold py-2" style={{ border: 'none' }}>{typeof s.gatewayId === 'object' ? (s.gatewayId as any).gatewayName : 'N/A'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                          <td className="text-muted fw-medium py-2" style={{ border: 'none' }}>📍 Location</td>
                          <td className="fw-semibold py-2" style={{ border: 'none' }}>{s.location || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted fw-medium py-2" style={{ border: 'none' }}>📅 Installed</td>
                          <td className="fw-semibold py-2" style={{ border: 'none' }}>{new Date(s.installationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0 d-flex justify-content-end gap-2 p-3" style={{ borderRadius: '0 0 16px 16px' }}>
                <Button variant="outline-secondary" size="sm" onClick={() => navigate(`/systems/${s.id}/reports`)} title="View reports" aria-label="View reports">
                  <BarChartIcon size={14} />
                </Button>
                {!isPreview && user?.role === 'customer' && (
                  <Button variant="primary" size="sm" className="btn-gradient btn-gradient-primary" onClick={() => handleOpenEdit(s)} title="Edit system" aria-label="Edit system">
                    <FiEdit2 size={14} />
                  </Button>
                )}
              </Card.Footer>
            </Card>
          </Col>
        ))}

        {systems.length === 0 && (
          <Col xs={12}>
            <div className="modern-card text-center py-5">
              <h5 className="text-muted">No systems found.</h5>
              <p className="text-muted small">Systems will appear here once they are assigned.</p>
            </div>
          </Col>
        )}
      </Row>
      </div>

      {/* Edit modal */}
      <Modal show={showEditModal} onHide={handleCloseEdit}>
        <Form onSubmit={handleUpdate}>
          <Modal.Header closeButton>
            <Modal.Title>Edit System Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>System Name</Form.Label>
              <Form.Control
                type="text"
                name="systemName"
                value={formData.systemName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Installation Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="installationDate"
                    value={formData.installationDate}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Alert variant="info">
              Primary product and gateway are fixed. Contact support for structural changes.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEdit}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default CustomerSystems;
